import { useState } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { Button } from '@/domains/shared/components/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/domains/shared/components/Card';
import { Progress } from '@/domains/shared/components/Progress';
import { RadioScale } from '@/domains/shared/components/RadioScale';
import { Input } from '@/domains/shared/components/Input';
import { ConsentCheckbox } from '@/domains/shared/components/ConsentCheckbox';
import { ScoreGauge } from '@/domains/shared/components/ScoreGauge';
import { CategoryBar } from '@/domains/shared/components/CategoryBar';
import { RadarChart } from '@/domains/shared/components/RadarChart';
import { Alert } from '@/domains/shared/components/Alert';
import { Modal } from '@/domains/shared/components/Modal';
import { Toast } from '@/domains/shared/components/Toast';
import type { DiagnosticScaleValue } from '@/domains/assessment/types';

export function DesignSystemPage() {
  const { navigate } = useRouter();

  // Estados locais para interatividade no catálogo
  const [scaleValue, setScaleValue] = useState<DiagnosticScaleValue | null>(2);
  const [consentA, setConsentA] = useState(true);
  const [consentB, setConsentB] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const sampleScores = {
    positioning: 44,
    founder_authority: 67,
    digital_assets: 55,
    distribution: 33,
    commercial_conversation: 78,
    customer_experience: 89,
  };

  const sampleTier = {
    min: 45,
    max: 64,
    key: 'building' as const,
    label: 'Presença em construção',
  };

  return (
    <div className="min-h-screen bg-navy text-brand-offWhite pb-16">
      {/* Header */}
      <header className="border-b border-white/10 bg-navy-dark px-4 py-4 sticky top-0 z-40 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="rounded bg-brand-orange px-2 py-0.5 text-xs font-black text-white">
              DS
            </span>
            <h1 className="text-sm font-bold text-white sm:text-base">
              Catálogo de Componentes & Design Tokens
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
              ← Voltar ao Início
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/diagnostico')}>
              Ver no Fluxo Real
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-8 space-y-12">
        {/* Seção 1: Tokens e Cores */}
        <section aria-labelledby="tokens-title">
          <h2 id="tokens-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            1. Tokens de Cores e Identidade (WCAG AA)
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-white/10 bg-navy p-4">
              <div className="h-10 w-full rounded-lg bg-[#000029] border border-white/20 mb-2" />
              <div className="text-xs font-bold text-white">Navy Predominante</div>
              <div className="text-[10px] text-brand-offWhite/60 font-mono">#000029</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-navy-light p-4">
              <div className="h-10 w-full rounded-lg bg-[#FE591F] mb-2" />
              <div className="text-xs font-bold text-white">Laranja DecolaCont</div>
              <div className="text-[10px] text-brand-offWhite/60 font-mono">#FE591F</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-navy-light p-4">
              <div className="h-10 w-full rounded-lg bg-[#F5F4EF] mb-2" />
              <div className="text-xs font-bold text-black">Off-White Contraste</div>
              <div className="text-[10px] text-brand-offWhite/60 font-mono">#F5F4EF</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-navy-light p-4">
              <div className="h-10 w-full rounded-lg bg-[#2E8B57] mb-2" />
              <div className="text-xs font-bold text-white">Sucesso / Tier Alto</div>
              <div className="text-[10px] text-brand-offWhite/60 font-mono">#2E8B57</div>
            </div>
          </div>
        </section>

        {/* Seção 2: Button (Todos os Estados) */}
        <section aria-labelledby="button-title">
          <h2 id="button-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            2. Componente Button (Variantes e Estados)
          </h2>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
              <Button variant="primary">Primary Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Danger</Button>
              <Button variant="success">Success</Button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <Button variant="primary" isLoading>Loading State</Button>
              <Button variant="primary" disabled>Disabled State</Button>
              <Button variant="secondary" size="sm">Small (sm)</Button>
              <Button variant="secondary" size="md">Medium (md)</Button>
              <Button variant="primary" size="lg">Large (lg)</Button>
            </div>
          </div>
        </section>

        {/* Seção 3: Inputs e Formulários */}
        <section aria-labelledby="form-title">
          <h2 id="form-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            3. Inputs e ConsentCheckboxes (Estados)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Input Normal (Default)" placeholder="Digite seu texto..." />
            <Input label="Input com Dica (Hint)" placeholder="contato@escritorio.com" hint="Utilize seu e-mail comercial principal." />
            <Input label="Input com Sucesso" value="Carlos Eduardo Mendes" success onChange={() => {}} />
            <Input label="Input com Erro (Aria-Invalid)" value="invalido" error="Informe um e-mail válido com @" onChange={() => {}} />
            <Input label="Input Desabilitado (Disabled)" value="Campo bloqueado" disabled />
          </div>

          <div className="mt-6 space-y-3">
            <ConsentCheckbox
              label="Checkbox Marcado (Default checked)"
              checked={consentA}
              onChange={(e) => setConsentA(e.target.checked)}
            />
            <ConsentCheckbox
              label="Checkbox com Erro de Aceite Obrigatório"
              checked={consentB}
              onChange={(e) => setConsentB(e.target.checked)}
              error={!consentB ? 'Você precisa marcar este item para continuar' : undefined}
            />
            <ConsentCheckbox
              label="Checkbox Desabilitado"
              checked={false}
              disabled
            />
          </div>
        </section>

        {/* Seção 4: RadioScale (0 a 3) */}
        <section aria-labelledby="radio-title">
          <h2 id="radio-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            4. Componente RadioScale (Alvo &gt;= 56px e Foco Visível)
          </h2>
          <div className="max-w-xl">
            <RadioScale
              name="demo-scale"
              value={scaleValue}
              onChange={(val) => setScaleValue(val)}
            />
            <p className="mt-2 text-xs text-brand-offWhite/60">
              Valor selecionado no estado reativo: <strong>{scaleValue}</strong>
            </p>
          </div>
        </section>

        {/* Seção 5: Progress e CategoryBar */}
        <section aria-labelledby="progress-title">
          <h2 id="progress-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            5. Progress e CategoryBar (Indicadores Semânticos)
          </h2>
          <div className="space-y-4 max-w-xl">
            <Progress value={65} currentStep={12} totalSteps={18} showPercentage />
            <CategoryBar
              categoryKey="positioning"
              name="Posicionamento (Prioridade Primária)"
              score={33}
              isPrimaryPriority
            />
            <CategoryBar
              categoryKey="distribution"
              name="Distribuição (Prioridade Secundária)"
              score={44}
              isSecondaryPriority
            />
            <CategoryBar
              categoryKey="customer_experience"
              name="Experiência do Cliente (Risco de Retenção)"
              score={22}
              hasRetentionRisk
            />
          </div>
        </section>

        {/* Seção 6: ScoreGauge e RadarChart Acessível */}
        <section aria-labelledby="charts-title">
          <h2 id="charts-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            6. ScoreGauge e RadarChart (Gráfico + Tabela Acessível)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-navy-light/30 p-6 rounded-2xl border border-white/10">
            <div className="flex justify-center">
              <ScoreGauge score={58} tier={sampleTier} size="md" />
            </div>
            <div>
              <RadarChart scores={sampleScores} />
            </div>
          </div>
        </section>

        {/* Seção 7: Alert, Card, Modal e Toast */}
        <section aria-labelledby="feedback-title">
          <h2 id="feedback-title" className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
            7. Alert, Modal, Card e Toast (Feedback de Ação)
          </h2>
          <div className="space-y-3">
            <Alert variant="info" title="Informação Institucional">
              Orientações baseadas no método de posicionamento contábil de Israel Costa.
            </Alert>
            <Alert variant="warning" title="Alerta Comercial">
              Sua conversa comercial necessita de revisão imediata antes do envio de propostas.
            </Alert>
            <Alert variant="danger" title="Risco Crítico de Retenção">
              Experiência do cliente abaixo de 40%: risco iminente de perda de carteira.
            </Alert>
            <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card variant="highlight">
                <CardHeader>
                  <CardTitle>Exemplo de Card Estruturado</CardTitle>
                  <CardDescription>Subtítulo ou contexto secundário do card</CardDescription>
                </CardHeader>
                <CardContent>
                  Demonstração dos slots modulares de conteúdo com contraste e espaçamento padronizado.
                </CardContent>
                <CardFooter>
                  <span className="text-xs text-brand-orange font-bold">Rodapé do Card</span>
                  <Button variant="outline" size="sm">Ação</Button>
                </CardFooter>
              </Card>

              <Card variant="interactive">
                <CardHeader>
                  <CardTitle>Card Interativo com Hover</CardTitle>
                  <CardDescription>Borda animada e transição suave</CardDescription>
                </CardHeader>
                <CardContent>
                  Utilizado nas seleções de dimensões e cards de lead no painel.
                </CardContent>
                <CardFooter>
                  <span className="text-xs text-brand-offWhite/60">Status: Ativo</span>
                </CardFooter>
              </Card>
            </div>

            <div className="pt-4 flex gap-3">
              <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                Abrir Modal Interativo
              </Button>
              <Button
                variant="secondary"
                onClick={() => setToastMessage('Ação realizada com sucesso no Design System!')}
              >
                Disparar Toast
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Modal Demonstrativo */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Componente Modal Acessível"
        description="Em conformidade com padrão WAI-ARIA com tecla ESC e backdrop focus trap."
        footer={
          <Button variant="primary" onClick={() => setIsModalOpen(false)}>
            Entendido
          </Button>
        }
      >
        <p className="text-sm text-brand-offWhite/80">
          Este modal é reutilizado no detalhe do lead administrativo, instruções e visualização detalhada.
        </p>
      </Modal>

      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
